import React, { useEffect, useState } from 'react';
import { WelcomePage } from './components/WelcomePage';
import { usePortletStore } from './App';
import { Message, MessageType } from './components/Message';
import AuthenticationWizard from './components/AuthenticationWizard';
import { getPageLogoUrl, intl, intlJSX } from 'ehs-react';
import AgreementPage from './components/AgreementPage';
import CommunicationUtil from './util/CommunicationUtil';
import type { modeText } from './util/AppModeUtil';
import { AppModes, getAppModeFromString } from './util/AppModeUtil';
import { CompleteUserAccountPage } from './components/CompleteUserAccountPage';
import { CreateUserAccountPage } from './components/CreateUserAccountPage';

const enum AppStates {
  CompleteUserAccount,
  Welcome,
  CreateUserAccount,
  Authentication,
  TermsAndConditions,
  DataProtectionRegulations
}

const expiredLinkText: modeText = {
  [AppModes.OnBoarding]: intl(
    'patientSelfOnboardingPortlet.errorAccountCreationLinkExpired'
  ),
  [AppModes.DocumentShare]: intl(
    'patientSelfOnboardingPortlet.errorLinkExpired'
  ),
  [AppModes.PasswordReset]: intl(
    'patientSelfOnboardingPortlet.errorLinkExpired'
  )
};

function getDefaultAppState(
  createUserAccountAllowed: boolean,
  completeUserAccountAllowed: boolean
): AppStates {
  if (createUserAccountAllowed) {
    return AppStates.Welcome;
  }
  return completeUserAccountAllowed
    ? AppStates.CompleteUserAccount
    : AppStates.Welcome;
}

export function MainView(): React.JSX.Element {
  const [
    referenceUserAvailable,
    accessLinkValid,
    referenceUserActive,
    referenceUserFirstName,
    onboardingCompleteUrl,
    completeUserAccountAllowed,
    createUserAccountAllowed,
    emailForSharedActiveAccount,
    appMode,
    initForm
  ] = usePortletStore(state => [
    state.configuration.referenceUserAvailable,
    state.configuration.accessLinkValid,
    state.configuration.referenceUserActive,
    state.configuration.referenceUserFirstName,
    state.configuration.onboardingCompleteUrl,
    state.configuration.completeUserAccountAllowed,
    state.configuration.createUserAccountAllowed,
    state.configuration.emailForSharedActiveAccount,
    getAppModeFromString(state.configuration.appMode),
    state.initForms
  ]);
  useEffect(() => initForm(), [initForm]); // init form values:

  const [currentState, setState] = useState(
    getDefaultAppState(createUserAccountAllowed, completeUserAccountAllowed)
  );

  function onPatientSelfOnboardingComplete() {
    window.location.href = onboardingCompleteUrl;
  }

  let componentToRender;
  if (currentState === AppStates.TermsAndConditions) {
    componentToRender = (
      <AgreementPage
        title={intl(
          'patientSelfOnboardingPortlet.welcomeScreen.termsAndConditionsTitle'
        )}
        operation={'getTermsAndConditions'}
        onBackClicked={() => setState(AppStates.Welcome)}
      />
    );
  } else if (currentState === AppStates.DataProtectionRegulations) {
    componentToRender = (
      <AgreementPage
        title={intl(
          'patientSelfOnboardingPortlet.welcomeScreen.dataProtectionRegulationsTitle'
        )}
        operation={'getDataProtectionRegulations'}
        onBackClicked={() => setState(AppStates.Welcome)}
      />
    );
  } else if (referenceUserAvailable) {
    if (!accessLinkValid) {
      componentToRender = (
        <Message type={MessageType.Error} text={expiredLinkText[appMode]} />
      );
    } else if (referenceUserActive && appMode !== AppModes.PasswordReset) {
      if (appMode === AppModes.DocumentShare) {
        componentToRender = (
          <Message
            type={MessageType.Default}
            text={
              <span>
                {intlJSX(
                  'patientSelfOnboardingPortlet.userAlreadyActiveTextWithEmail',
                  {
                    0: (
                      <span className="bold">
                        {emailForSharedActiveAccount}
                      </span>
                    )
                  }
                )}
              </span>
            }
            button={{
              text: intl(
                'patientSelfOnboardingPortlet.userAlreadyActiveButtonProceed'
              ),
              onClick: onPatientSelfOnboardingComplete
            }}
          />
        );
      } else {
        componentToRender = (
          <Message
            type={MessageType.Info}
            text={intl('patientSelfOnboardingPortlet.userAlreadyActiveText')}
            button={{
              text: intl(
                'patientSelfOnboardingPortlet.userAlreadyActiveButtonContinue'
              ),
              onClick: onPatientSelfOnboardingComplete
            }}
          />
        );
      }
    } else if (currentState === AppStates.CompleteUserAccount) {
      componentToRender = <CompleteUserAccountPage />;
    } else if (currentState === AppStates.Welcome) {
      componentToRender = (
        <WelcomePage
          onShowTermsAndConditionsClicked={() =>
            setState(AppStates.TermsAndConditions)
          }
          onShowDataProtectionRegulationsClicked={() =>
            setState(AppStates.DataProtectionRegulations)
          }
          onStartClicked={async () => {
            if (appMode === AppModes.PasswordReset) {
              await CommunicationUtil.sendDeactivateUserAccountRequest();
            }
            setState(AppStates.Authentication);
          }}
          userName={referenceUserFirstName}
          canSaveOnboardingAgreements={true}
        />
      );
    } else if (currentState === AppStates.Authentication) {
      componentToRender = (
        <AuthenticationWizard
          onWizardExecuted={() => onPatientSelfOnboardingComplete()}
        />
      );
    } else {
      componentToRender = (
        <Message
          type={MessageType.Error}
          text={intl('patientSelfOnboardingPortlet.errorInvalidAppState')}
        />
      );
    }
  } else if (createUserAccountAllowed) {
    if (currentState === AppStates.Welcome) {
      componentToRender = (
        <WelcomePage
          onShowTermsAndConditionsClicked={() =>
            setState(AppStates.TermsAndConditions)
          }
          onShowDataProtectionRegulationsClicked={() =>
            setState(AppStates.DataProtectionRegulations)
          }
          onStartClicked={() => {
            setState(AppStates.CreateUserAccount);
          }}
          userName={referenceUserFirstName}
          canSaveOnboardingAgreements={false}
          backToLoginUrl={onboardingCompleteUrl}
        />
      );
    } else if (currentState === AppStates.CreateUserAccount) {
      componentToRender = (
        <CreateUserAccountPage
          onAccountCreated={() => {
            setState(AppStates.Authentication);
          }}
          onBackClicked={() => {
            setState(AppStates.Welcome);
          }}
        />
      );
    } else if (currentState === AppStates.Authentication) {
      componentToRender = (
        <AuthenticationWizard
          onWizardExecuted={() => onPatientSelfOnboardingComplete()}
          onBackClicked={() => setState(AppStates.CreateUserAccount)}
        />
      );
    } else {
      componentToRender = (
        <Message
          type={MessageType.Error}
          text={intl('patientSelfOnboardingPortlet.errorInvalidAppState')}
        />
      );
    }
  } else {
    componentToRender = (
      <Message
        type={MessageType.Error}
        text={intl('patientSelfOnboardingPortlet.errorNotAuthorized')}
      />
    );
  }

  return (
    <div className={'patient-self-onboarding-portlet'}>
      <div className="left-panel">
        <header className="welcome-logo-container" role="banner">
          <a href={onboardingCompleteUrl} title={intl('common.link.loginpage')}>
            <img
              className="welcome-logo"
              src={getPageLogoUrl()}
              alt={`${intl('common.logo.tooltip')} ${SenseConstants.company}`}
            />
          </a>
        </header>
        <div className="render-component" role="main">
          {componentToRender}
        </div>
      </div>
      <div className="right-panel image-column-right" />
    </div>
  );
}
